import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { products } from '@/mocks/products';
import ProductCard from '@/components/base/ProductCard';
import FilterSidebar from './components/FilterSidebar';

const PRICE_MIN = 249;
const PRICE_MAX = 1599;

const FUNCTION_ID_TO_NAME: Record<string, string> = {
  'chew-dental': 'Chew & Dental',
  'enrichment-puzzle': 'Enrichment & Puzzle',
  'fetch-active': 'Fetch & Active Play',
  'comfort-snuggle': 'Comfort & Snuggle',
  'everyday-accessories': 'Everyday Accessories',
  'gifting-edits': 'Gifting Edits',
};

const MATERIAL_ID_MATCH = (matId: string, productMaterial: string): boolean => {
  if (matId === 'natural-rubber') return productMaterial === 'Natural Rubber';
  if (matId === 'jute-hemp') return productMaterial.includes('Jute') || productMaterial.includes('Hemp');
  if (matId === 'coconut-coir') return productMaterial === 'Coconut Coir';
  if (matId === 'bamboo') return productMaterial === 'Bamboo';
  if (matId === 'organic-cotton') return productMaterial === 'Organic Cotton';
  if (matId === 'silicone') return productMaterial === 'Silicone';
  return false;
};

const PET_SUBCATEGORY_MAP: Record<string, string> = {
  'puppy': 'Puppy',
  'adult-moderate': 'Adult – Moderate Chewer',
  'adult-aggressive': 'Adult – Aggressive Chewer',
  'senior': 'Senior',
  'kitten-adult': 'Kitten / Adult',
};

const PET_TYPE_IDS = ['dog', 'cat', 'universal'];

function getDisplayPrice(product: typeof products[number]): number {
  if (product.variants && product.variants.length > 0) {
    return Math.min(...product.variants.map(v => v.price));
  }
  return product.price;
}

function getTierFromPrice(price: number): string {
  if (price < 400) return 'essentials';
  if (price < 800) return 'signature';
  if (price < 1200) return 'premium-enrichment';
  return 'gifting-sets';
}

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const filters = useMemo(() => ({
    pet: searchParams.get('pet')?.split(',').filter(Boolean) ?? [],
    functionCategory: searchParams.get('function')?.split(',').filter(Boolean) ?? [],
    material: searchParams.get('material')?.split(',').filter(Boolean) ?? [],
    priceMin: Number(searchParams.get('priceMin')) || PRICE_MIN,
    priceMax: Number(searchParams.get('priceMax')) || PRICE_MAX,
    pricingTier: searchParams.get('tier')?.split(',').filter(Boolean) ?? [],
    sort: searchParams.get('sort') || 'featured',
  }), [searchParams]);

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    const current = next.get(key)?.split(',').filter(Boolean) ?? [];
    const idx = current.indexOf(value);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(value);
    }
    if (current.length > 0) {
      next.set(key, current.join(','));
    } else {
      next.delete(key);
    }
    setSearchParams(next);
  };

  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (filters.pet.length > 0) {
      result = result.filter(p => {
        return filters.pet.some(f => {
          if (PET_TYPE_IDS.includes(f)) {
            return p.petType === f;
          }
          return PET_SUBCATEGORY_MAP[f] && p.petSubcategory === PET_SUBCATEGORY_MAP[f];
        });
      });
    }

    if (filters.functionCategory.length > 0) {
      result = result.filter(p => {
        return filters.functionCategory.some(f => {
          return FUNCTION_ID_TO_NAME[f] === p.functionCategory;
        });
      });
    }

    if (filters.material.length > 0) {
      result = result.filter(p => {
        return filters.material.some(f => MATERIAL_ID_MATCH(f, p.material));
      });
    }

    result = result.filter(p => {
      const price = getDisplayPrice(p);
      return price >= filters.priceMin && price <= filters.priceMax;
    });

    if (filters.pricingTier.length > 0) {
      result = result.filter(p => {
        const price = getDisplayPrice(p);
        return filters.pricingTier.some(t => getTierFromPrice(price) === t);
      });
    }

    switch (filters.sort) {
      case 'trending':
        result.sort((a, b) => (b.isTrending ? 1 : 0) - (a.isTrending ? 1 : 0));
        break;
      case 'newest':
        result.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
        break;
      case 'price-asc':
        result.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
        break;
      case 'best-rated':
        result.sort((a, b) => b.rating - a.rating);
        break;
    }

    return result;
  }, [filters]);

  const handleClearAll = () => {
    setSearchParams({});
  };

  const hasActiveFilters =
    filters.pet.length > 0 ||
    filters.functionCategory.length > 0 ||
    filters.material.length > 0 ||
    filters.priceMin !== PRICE_MIN ||
    filters.priceMax !== PRICE_MAX ||
    filters.pricingTier.length > 0;

  return (
    <div className="bg-background-50 min-h-screen">
      {/* Page header */}
      <div className="pt-24 pb-6 px-4 md:px-8 lg:px-12">
        <div className="max-w-screen-2xl mx-auto">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
            <span className="w-7 h-7 inline-flex items-center justify-center mr-2">
              <i className="ri-store-2-line text-accent-500 text-2xl"></i>
            </span>
            All Products
          </h1>
          <p className="text-foreground-500 mt-2 text-sm md:text-base max-w-xl">
            Every toy, accessory, and gift set — made from materials your pet can actually trust. Filter by pet, function, material, or price to find exactly what you need.
          </p>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12 pb-16">
        <div className="max-w-screen-2xl mx-auto flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-24">
              <FilterSidebar
                filters={filters}
                onTogglePet={(id) => updateFilter('pet', id)}
                onToggleFunction={(id) => updateFilter('function', id)}
                onToggleMaterial={(id) => updateFilter('material', id)}
                onToggleTier={(id) => updateFilter('tier', id)}
                onPriceChange={(min, max) => {
                  const next = new URLSearchParams(searchParams);
                  if (min === PRICE_MIN) next.delete('priceMin');
                  else next.set('priceMin', String(min));
                  if (max === PRICE_MAX) next.delete('priceMax');
                  else next.set('priceMax', String(max));
                  setSearchParams(next);
                }}
                onClearAll={handleClearAll}
              />
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0">
            {/* Toolbar: result count, active tags, sort, mobile filter button */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-foreground-600 font-medium">
                  <strong className="text-foreground-900">{filteredProducts.length}</strong> product{filteredProducts.length !== 1 ? 's' : ''}
                </span>

                {/* Mobile filter button */}
                <button
                  onClick={() => setMobileFilterOpen(true)}
                  className="lg:hidden flex items-center gap-1.5 px-3 py-1.5 bg-secondary-100 text-foreground-700 rounded-full text-xs font-semibold hover:bg-secondary-200 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-equalizer-line"></i>
                  </span>
                  Filters
                  {hasActiveFilters && (
                    <span className="w-4 h-4 flex items-center justify-center bg-accent-500 text-background-50 rounded-full text-[10px] font-bold">
                      !
                    </span>
                  )}
                </button>

                {/* Active filter tags */}
                {hasActiveFilters && (
                  <button
                    onClick={handleClearAll}
                    className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-accent-500 hover:text-accent-600 transition-colors duration-150 cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-close-circle-line"></i>
                    </span>
                    Clear all
                  </button>
                )}
              </div>

              {/* Sort dropdown */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500 hidden sm:inline">Sort by:</span>
                <select
                  value={filters.sort}
                  onChange={(e) => {
                    const next = new URLSearchParams(searchParams);
                    if (e.target.value === 'featured') {
                      next.delete('sort');
                    } else {
                      next.set('sort', e.target.value);
                    }
                    setSearchParams(next);
                  }}
                  className="px-3 py-1.5 pr-8 bg-secondary-100 text-foreground-700 rounded-full text-xs font-medium border-none outline-none cursor-pointer appearance-none bg-[length:16px] bg-[right_8px_center] bg-no-repeat"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                  }}
                >
                  <option value="featured">Featured</option>
                  <option value="trending">Trending</option>
                  <option value="newest">Newest</option>
                  <option value="price-asc">Price: Low – High</option>
                  <option value="best-rated">Best Rated</option>
                </select>
              </div>
            </div>

            {/* Sort chip row */}
            <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] -mx-4 px-4 md:mx-0 md:px-0">
              {[
                { value: 'featured', label: 'Featured', icon: 'ri-star-line' },
                { value: 'trending', label: 'Trending', icon: 'ri-fire-line' },
                { value: 'newest', label: 'Newest', icon: 'ri-sparkling-line' },
                { value: 'price-asc', label: 'Price: Low – High', icon: 'ri-sort-asc' },
                { value: 'best-rated', label: 'Best Rated', icon: 'ri-medal-line' },
              ].map((chip) => {
                const isActive = filters.sort === chip.value;
                return (
                  <button
                    key={chip.value}
                    onClick={() => {
                      const next = new URLSearchParams(searchParams);
                      if (chip.value === 'featured') {
                        next.delete('sort');
                      } else {
                        next.set('sort', chip.value);
                      }
                      setSearchParams(next);
                    }}
                    className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0 ${
                      isActive
                        ? 'bg-accent-500 text-background-50 shadow-sm'
                        : 'bg-secondary-100 text-foreground-600 hover:bg-secondary-200 hover:text-foreground-800'
                    }`}
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={chip.icon}></i>
                    </span>
                    {chip.label}
                  </button>
                );
              })}
            </div>

            {/* Product Grid or Empty State */}
            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {filteredProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-secondary-100 mb-5">
                  <i className="ri-footprint-line text-3xl text-foreground-300"></i>
                </div>
                <h3 className="font-heading text-xl font-bold text-foreground-800 mb-2">
                  No products match
                </h3>
                <p className="text-sm text-foreground-500 max-w-sm mb-6">
                  Try adjusting or clearing some filters — there might be something your pet will love hiding just around the corner.
                </p>
                <button
                  onClick={handleClearAll}
                  className="px-5 py-2.5 bg-accent-500 text-background-50 rounded-full font-semibold text-sm hover:bg-accent-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  Clear All Filters
                </button>
              </div>
            )}

            {/* Product count at bottom */}
            {filteredProducts.length > 0 && filteredProducts.length < products.length && (
              <p className="text-xs text-foreground-400 text-center mt-8">
                Showing {filteredProducts.length} of {products.length} products with active filters
              </p>
            )}
          </main>
        </div>
      </div>

      {/* Mobile filter drawer overlay */}
      {mobileFilterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileFilterOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-background-50 shadow-lg overflow-y-auto animate-slide-in-left">
            <div className="p-5">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg font-bold text-foreground-900">Filters</h3>
                <button
                  onClick={() => setMobileFilterOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-secondary-100 text-foreground-500 transition-colors duration-150 cursor-pointer"
                  aria-label="Close filters"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <FilterSidebar
                filters={filters}
                onTogglePet={(id) => updateFilter('pet', id)}
                onToggleFunction={(id) => updateFilter('function', id)}
                onToggleMaterial={(id) => updateFilter('material', id)}
                onToggleTier={(id) => updateFilter('tier', id)}
                onPriceChange={(min, max) => {
                  const next = new URLSearchParams(searchParams);
                  if (min === PRICE_MIN) next.delete('priceMin');
                  else next.set('priceMin', String(min));
                  if (max === PRICE_MAX) next.delete('priceMax');
                  else next.set('priceMax', String(max));
                  setSearchParams(next);
                }}
                onClearAll={() => {
                  handleClearAll();
                  setMobileFilterOpen(false);
                }}
              />
              <button
                onClick={() => setMobileFilterOpen(false)}
                className="mt-6 w-full py-3 bg-accent-500 text-background-50 rounded-full font-semibold text-sm hover:bg-accent-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                Show Results ({filteredProducts.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Slide-in animation keyframes */}
      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-left {
          animation: slideInLeft 0.25s ease-out;
        }
      `}</style>
    </div>
  );
}